import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle, XCircle, X } from "lucide-react";
import { useSuperAdmin } from "../context/SuperAdminContext";
import { formatTimePart } from "../helpers/change_payout_date_helper";
import { MODES, toApiDate, getTomorrowISO, formatDate } from "./paymentSettingUtils";

/**
 * Modal payout settings screen for an admin/superadmin changing a worker's
 * payout mode. Requires the target worker's id via `workerId`.
 * Controlled via `isOpen`/`onClose`, matching the Paymentwithdraw modal pattern.
 */
const AdminPaymentSetting = ({ isOpen, onClose, workerId, initialPayoutFrequency ,fetchWorkerDetails}) => {
  const { changepayoutmode, worker_payout_history } = useSuperAdmin();

  const [selected, setSelected] = useState(initialPayoutFrequency ?? null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: "success", message: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [appliedFrom, setAppliedFrom] = useState(getTomorrowISO());
  const [dateError, setDateError] = useState("");

  const minDate = getTomorrowISO();

  const loadHistory = useCallback(
    async (id) => {
      if (!id) return;
      setLoadingHistory(true);
      try {
        const res = await worker_payout_history(id);
        setSelected(res?.data?.payoutFrequency ?? null);
        setHistory(res?.data?.history ? [...res.data.history] : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingHistory(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen && workerId) {
      loadHistory(workerId);
    }
  }, [isOpen, workerId, loadHistory]);

  // Reset transient UI state whenever the modal is closed, so re-opening it
  // (possibly for a different worker) doesn't show stale confirm/notification state.
  useEffect(() => {
    if (!isOpen) {
      setConfirmOpen(false);
      setPendingMode(null);
      setDateError("");
      setNotification((prev) => (prev.show ? { ...prev, show: false } : prev));
    }
  }, [isOpen]);

  function showNotification(type, message) {
    setNotification({ show: true, type, message });
  }

  function closeNotification() {
    setNotification((prev) => ({ ...prev, show: false }));
  }

  useEffect(() => {
    if (!notification.show) return;
    const timer = setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 4000);
    return () => clearTimeout(timer);
  }, [notification.show, notification.message]);

  function renderNotification() {
    if (!notification.show) return null;
    const isSuccess = notification.type === "success";
    return (
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1200] w-full max-w-sm px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center px-4 py-3">
            {isSuccess ? (
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" strokeWidth={0} />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" fill="currentColor" strokeWidth={0} />
            )}
            <p className="ml-3 flex-1 text-sm font-medium text-gray-700">{notification.message}</p>
            <button onClick={closeNotification} className="ml-3 flex-shrink-0">
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          </div>
          <div className="h-1 w-full bg-gray-100">
            <div
              key={notification.message}
              className={`h-full ${isSuccess ? "bg-green-500" : "bg-red-500"} animate-[toastShrink_4s_linear_forwards]`}
            />
          </div>
        </div>
        <style>{`
          @keyframes toastShrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  function handleModeClick(id) {
    if (id === selected) return; // already active, nothing to confirm
    setPendingMode(id);
    setAppliedFrom(getTomorrowISO());
    setDateError("");
    setConfirmOpen(true);
  }

  function closeConfirm() {
    if (submitting) return; // don't allow closing mid-request
    setConfirmOpen(false);
    setPendingMode(null);
    setDateError("");
  }

  function handleDateChange(e) {
    const value = e.target.value;
    setAppliedFrom(value);
    if (value && value < minDate) {
      setDateError("Please select a future date.");
    } else {
      setDateError("");
    }
  }

  function handleClose() {
    if (submitting) return; // don't allow closing mid-request
    if (onClose) onClose();
  }

  async function handleConfirmChange() {
    if (!pendingMode) return;
    if (!appliedFrom || appliedFrom < minDate) {
      setDateError("Please select a future date.");
      return;
    }

    setSubmitting(true);
    try {
      await changepayoutmode(pendingMode, toApiDate(appliedFrom), workerId);
      setSelected(pendingMode);
      await loadHistory(workerId);
      showNotification("success", `Payout mode changed to ${pendingMode}, applied from ${formatDate(appliedFrom)}.`);
      setConfirmOpen(false);
      setPendingMode(null);
    } catch (err) {
      showNotification("error", "Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={handleClose}>
      <div
        className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900">Change Payout Mode</h2>
          <button
            type="button"
           onClick={() => {
  handleClose();
  fetchWorkerDetails();
}}
            aria-label="Close"
            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-50"
          >
            ✕
          </button>
        </div>

        <div className="px-5 pb-10">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Preferred Payout mode</h3>

          <div className="space-y-1.5">
            {MODES.map(({ id, title, desc, icon }) => {
              const active = selected === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleModeClick(id)}
                  className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-colors ${
                    active ? "border-violet-500 bg-[#FBF9FD]" : "border-gray-200 shadow-gray-400 bg-[#FBF9FD]"
                  }`}
                >
                  <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-base ${
                      active ? "bg-violet-300 text-white" : "bg-gray-100"
                    }`}
                  >
                    <img src={icon} alt="" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-bold text-gray-900">{title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
                  </span>
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      active ? "border-violet-600" : "border-gray-300"
                    }`}
                  >
                    {active && <span className="w-2.5 h-2.5 rounded-full bg-violet-600" />}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="pb-2">
            <h3 className="text-sm font-semibold text-gray-700 mt-3">Payout Mode History</h3>
          </div>

          {loadingHistory ? (
            <div className="text-sm text-gray-400 py-6 text-center">Loading history...</div>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No changes yet.</p>
          ) : (
            <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
              {history.map((h) => (
                <div key={h._id} className="rounded-xl border border-gray-100 bg-white shadow-sm px-4 py-3">
                  <div className="flex justify-between items-center text-xs text-gray-400">
                    <span>
                      On {formatDate(h.doneAt)} {formatTimePart(h.doneAt)}
                    </span>
                    {h.appliedFrom && <span>Applied from: {formatDate(h.appliedFrom)}</span>}
                  </div>
                  <p className="text-sm text-gray-700 mt-1">
                    The payout mode was changed from{" "}
                    <span className="font-semibold text-gray-900 capitalize">{h.oldValue}</span> to{" "}
                    <span className="font-semibold text-gray-900 capitalize">{h.newValue}.</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-5" onClick={closeConfirm}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900">Confirm payout mode change</h3>
            <p className="text-sm text-gray-500 mt-1.5">
              Are you sure you want to change your payout mode to{" "}
              <span className="font-semibold text-gray-900">{pendingMode}</span>?
            </p>

            <label className="block text-xs font-semibold text-gray-700 mt-4 mb-1.5">Applied from</label>
            <input
              type="date"
              value={appliedFrom}
              min={minDate}
              onChange={handleDateChange}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
            {dateError && <p className="text-xs text-red-500 mt-1.5">{dateError}</p>}

            <div className="flex gap-2.5 mt-5">
              <button
                type="button"
                onClick={closeConfirm}
                disabled={submitting}
                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmChange}
                disabled={submitting || !!dateError}
                className="flex-1 rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderNotification()}
    </div>
  );
};

export default AdminPaymentSetting;